import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-admin';
import { Box, Typography } from '@mui/material';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import { shortDateTime } from '../../shared/dateTime';
import type { DriverWeek, PlanItem, PlanMonth, PlanSegmentKind } from './vehiclePlanApi';
import {
  LEGEND_SEGMENTS,
  hasSegments,
  occupiedUntilLine,
  segmentNotesLines,
  segmentTitle,
  tmsPlanTitle,
} from './planSegments';
import { driverWeekBounds, driverWeekText, driverWeekTitle, isReplicatedWeek } from './driverWeeks';
import { unavailableMonthTitle } from './carScheduleApi';
import { PLAN_PHASES, planPhase, planRowStatus, tmsStatusText, type PlanPhase } from './planRows';
import {
  barGeometry,
  nowOffset,
  packLanes,
  planItemSpan,
  routeText,
  timelineDays,
  type TimelineWindow,
} from './timeline';
import { useFeature } from '../featureFlags/FeatureGate';
import { GANTT_WINDOW_BRACKETS, OCCUPIED_UNTIL_TMS_PLAN, VEHICLE_CARD_PROTOTYPE } from './fleetFeatures';
import { WindowBrackets } from './WindowBrackets';
import { BRACKET_COLORS, BRACKET_LINE_PX, bracketsExtent } from './bracketRules';
import { fitBarLabel, labelWidthPx, lateSpan } from './ganttRules';
import { useElementWidth } from './useElementWidth';
import { latenessLines, latenessOf } from '../userOffers/variantWindow';

/**
 * Wygląd osi według przełączników konta (decyzja użytkownika 12.10.2026,
 * wersja „obecna” = produkcja 0.31.3):
 * - `prototypeColors` (`vehicleCardPrototype`) — kolory linii jak w prototypie
 *   karty pojazdu; wyłączony — zaplanowane szare, zrealizowane zielone,
 *   wariant w pomarańczowej ramce, jak przed 07.10.2026;
 * - `tmsPlan` (`occupiedUntilTmsPlan`) — odcinek „wg planu TMS” i linia
 *   „Zajęty do” w dymku; wyłączony — bez nich, jak przed 12.10.2026.
 */
export interface TimelineLook {
  prototypeColors: boolean;
  tmsPlan: boolean;
}

/** Wygląd osi dla zalogowanego (przełączniki z API). */
export function useTimelineLook(): TimelineLook {
  const prototypeColors = useFeature(VEHICLE_CARD_PROTOTYPE);
  const tmsPlan = useFeature(OCCUPIED_UNTIL_TMS_PLAN);
  return { prototypeColors, tmsPlan };
}

/** Wygląd z nowej wersji — karta pojazdu wg prototypu (pokazywana tylko z nim). */
const NEW_LOOK: TimelineLook = { prototypeColors: true, tmsPlan: true };

/**
 * Oś harmonogramu — wspólna dla karty auta i harmonogramu floty.
 *
 * ## Trzy linie (omówienie 01.10.2026, [1:31:10–1:37:04])
 *
 * „3 takie linie — to, które zrealizowaliśmy, to, które realizujemy, i to,
 * które będzie realizowane”. Pozycja trafia do linii po stanie z TMS
 * (`planRows.planPhase`): zrealizowane, w realizacji, zaplanowane (razem
 * z wariantami). Karta auta ma trzy podpisane wiersze; harmonogram floty —
 * trzy cienkie linie w wierszu każdego auta (`splitPhases`).
 *
 * Każda pozycja to dwie warstwy:
 * - **okno zlecenia od klienta** — szeroki obszar linią przerywaną (od
 *   początku okna załadunku do końca okna rozładunku);
 * - **szacunek wykonania** — pełny pasek wewnątrz okna: szacowana zajętość
 *   auta (`occupancy`, ta sama reguła co w podpowiedziach). Kolor z linii
 *   (od 07.10.2026, prototyp karty pojazdu, przełącznik `vehicleCardPrototype`):
 *   w realizacji niebieski, zaplanowane zielone, zrealizowane szare; bez
 *   przełącznika — zaplanowane szare, zrealizowane zielone (`phaseFill`).
 *   Wariant (wstępne wskazanie, §15 FTL) — obramowanie przerywane.
 *
 * Od 06.10.2026 pasek szacunku jest rozpisany na **odcinki** (omówienie
 * 01.10: „z odpoczynkiem kierowcy, dolotem, załadunkiem ok. 2 h, przejazdem
 * i rozładunkiem”): odcień i wzór w kolorze linii mówią, co to za odcinek
 * (`segmentFill`), dymek każdego — godziny, długość, źródło i podstawę
 * (`planSegments.ts`). Dolot stoi przed paskiem zajętości, odcinki kończą
 * się dokładnie na jego końcu (reguła w API). Starsze API bez odcinków —
 * pasek jednym blokiem, jak dotąd.
 *
 * Nachodzące pozycje idą na osobne podtory (po oknie i pasku naraz), żeby
 * konflikt był widać, a nie żeby jedna zasłaniała drugą. Pasek prowadzi do
 * zlecenia. Miesiąc z niedostępnością (serwis) jest zakreskowany — kontekst
 * z okien miesięcznych, gdy istnieją.
 *
 * Wiersz z `driverWeeks` to cienki tor kierowcy z harmonogramu kierowców:
 * odcinek na tydzień, wpis z pliku obramowany, powielony — szary, kursywą.
 *
 * Przewija się **wyłącznie kontener osi** (1366 px: menu + etykiety + dni
 * nie mieszczą się naraz); etykieta wiersza stoi przyklejona z lewej.
 */

export interface TimelineRow {
  key: string | number;
  label: ReactNode;
  items: PlanItem[];
  months?: PlanMonth[];
  /** Tor kierowcy z harmonogramu — zamiast pasków zleceń. */
  driverWeeks?: DriverWeek[];
  /** Pozycje w trzech liniach wiersza: zrealizowane, w realizacji, zaplanowane. */
  splitPhases?: boolean;
  /** Komórki kolumn dodatkowych (`extraColumns`) — w tej samej kolejności. */
  extra?: ReactNode[];
  /** Luki w planie (harmonogram floty wg makiety, 15.10.2026) — klikalne. */
  gaps?: TimelineGap[];
}

/** Luka w planie auta na osi — klikalny obszar pod paskami. */
export interface TimelineGap {
  key: string;
  from: string;
  to: string;
  label: string;
  title: string;
  /** Okno z listą (pasujące zlecenia) albo — bez niego — odnośnik `link`. */
  onClick?: () => void;
  link?: string;
}

/** Kolumna po prawej stronie osi (harmonogram floty wg makiety). */
export interface TimelineColumn {
  key: string;
  header: ReactNode;
  width: number;
}

/**
 * Dymek odcinka spóźnienia: koniec szacunku i — z przełącznikiem „wariant
 * tylko w oknach” — obie liczby spóźnienia z opisem źródła (22.10.2026,
 * decyzja użytkownika pkt 4).
 */
export function lateBarTitle(item: PlanItem, late: { from: string; to: string }): string {
  const head = `Spóźnienie wg szacunku: wykonanie skończy się ${shortDateTime(late.to)}, po końcu okna rozładunku (${shortDateTime(late.from)}).`;
  const both = latenessOf(item.lateness);
  return both ? [head, ...latenessLines(both).map((l) => `– ${l}`)].join('\n') : head;
}

/**
 * Odcinek spóźnienia za oknem rozładunku (`overrunUntil`) — na osi floty
 * z układem wg makiety (15.10.2026); ta sama reguła co karta pojazdu
 * (`ganttRules.lateSpan`). Kreskowany pomarańcz — ostrzeżenie.
 */
const LateBar = ({
  item,
  window,
  top,
  height,
}: {
  item: PlanItem;
  window: TimelineWindow;
  top: number;
  height: number;
}) => {
  const late = lateSpan(item);
  if (!late) return null;
  const g = barGeometry(late.from, late.to, window, 0);
  if (!g || g.width <= 0) return null;
  return (
    <Box
      data-testid={`plan-late-${item.kind}-${item.orderId}`}
      title={lateBarTitle(item, late)}
      sx={{
        position: 'absolute',
        left: `${g.left}%`,
        width: `${g.width}%`,
        top,
        height,
        boxSizing: 'border-box',
        border: '1px solid',
        borderColor: 'warning.dark',
        borderLeft: 0,
        borderRadius: '0 4px 4px 0',
        backgroundImage: 'repeating-linear-gradient(135deg, #ed6c02 0 6px, #f7a65a 6px 12px)',
        zIndex: 2,
      }}
    />
  );
};

/** Luka w planie — klikalny obszar na całą wysokość wiersza, pod paskami. */
const GapArea = ({ gap, window }: { gap: TimelineGap; window: TimelineWindow }) => {
  const g = barGeometry(gap.from, gap.to, window, 0);
  if (!g || g.width <= 0) return null;
  const { onClick } = gap;
  const sx = {
    position: 'absolute',
    left: `${g.left}%`,
    width: `${g.width}%`,
    top: 1,
    bottom: 1,
    boxSizing: 'border-box',
    border: '1px dashed',
    borderColor: 'success.main',
    borderRadius: 1,
    bgcolor: (theme: Theme) => alpha(theme.palette.success.main, 0.06),
    cursor: 'pointer',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    px: 0.5,
    fontSize: '0.68rem',
    color: 'success.dark',
    textDecoration: 'none',
    display: 'block',
    zIndex: 1,
    '&:hover, &:focus-visible': { bgcolor: (theme: Theme) => alpha(theme.palette.success.main, 0.16) },
  } as const;
  if (!onClick) {
    return (
      <Link to={gap.link ?? '#'} title={gap.title} aria-label={gap.label} data-testid={`plan-gap-${gap.key}`} sx={sx}>
        {gap.label}
      </Link>
    );
  }
  return (
    <Box
      role="button"
      tabIndex={0}
      title={gap.title}
      aria-label={gap.label}
      data-testid={`plan-gap-${gap.key}`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      sx={sx}
    >
      {gap.label}
    </Box>
  );
};

/**
 * Wymiary linii. Karta auta — wyższa (omówienie 01.10.2026: „ten gant mógłby
 * być wyższy zdecydowanie, bo na nim wszystko lepiej widać”); flota —
 * zwarta, bo aut są setki, a każde ma trzy linie. Od 06.10.2026 obie wyższe
 * (karta 30 → 40 px, flota 18 → 24 px), żeby odcinki paska — kreskowany
 * dolot, paski ukośne odpoczynku — dało się odróżnić. Szerokość dni bez
 * zmian: flota przy 1366 px dalej mieści się bez przewijania w bok.
 */
const SIZES = {
  large: { lane: 40, inset: 4, font: '0.75rem', fontPx: 12 },
  compact: { lane: 24, inset: 2, font: '0.68rem', fontPx: 10.88 },
} as const;

/**
 * Podpis paska dopasowany do szerokości (29.10.2026, decyzja Bartka „oś
 * czasu — popraw”): numer na wąskim pasku był ucinany z końca, a to koniec
 * odróżnia zlecenia. `fitBarLabel` daje pełny podpis albo koniec numeru —
 * z dopiskiem „wariant”, gdy jest (30.10.2026); gdy nic się nie mieści —
 * koniec numeru z dopiskiem obok paska (za nim albo przed nim). Bez szerokości osi (testy) — pełny podpis, jak dotąd.
 */
function barLabelParts(
  g: { left: number; width: number; clippedStart: boolean },
  item: PlanItem,
  suffix: string,
  trackWidth: number,
  fontPx: number,
): { inside: string; outside: { leftPct: number; text: string } | null } {
  const orderNumber = `${g.clippedStart ? '‹ ' : ''}${item.number ?? `#${item.orderId}`}`;
  const px = (g.width * trackWidth) / 100;
  // Odstępy paska: 2 × 4 px.
  const fitted = fitBarLabel(orderNumber, suffix, trackWidth > 0 ? Math.max(px - 8, 0.01) : 0, fontPx);
  if (!fitted.outside) return { inside: fitted.text, outside: null };
  const w = labelWidthPx(fitted.text, fontPx) + 4;
  const rightPx = ((g.left + g.width) * trackWidth) / 100;
  const leftPx = rightPx + w <= trackWidth ? rightPx + 2 : Math.max((g.left * trackWidth) / 100 - w - 2, 0);
  return { inside: '', outside: { leftPct: (leftPx / trackWidth) * 100, text: fitted.text } };
}

/** Podpis obok paska, który go nie mieści — bez kursora, nad tłem osi. */
const OutsideLabel = ({
  label,
  top,
  height,
  font,
  testId,
}: {
  label: { leftPct: number; text: string };
  top: number;
  height: number;
  font: string;
  testId: string;
}) => (
  <Box
    aria-hidden
    data-testid={testId}
    sx={{
      position: 'absolute',
      left: `${label.leftPct}%`,
      top,
      height,
      lineHeight: `${height - 2}px`,
      px: '2px',
      fontSize: font,
      whiteSpace: 'nowrap',
      color: 'text.primary',
      pointerEvents: 'none',
      zIndex: 2,
    }}
  >
    {label.text}
  </Box>
);
export type TimelineSize = keyof typeof SIZES;

const LANE_GAP = 3;
const DRIVER_ROW_HEIGHT = 20;

export const DriverWeekSegment = ({ week, window }: { week: DriverWeek; window: TimelineWindow }) => {
  const bounds = driverWeekBounds(week);
  // Koniec tygodnia to początek następnego — o milisekundę wcześniej, żeby
  // sąsiednie odcinki się stykały, a nie nachodziły.
  const g = barGeometry(bounds.from, new Date(bounds.to.getTime() - 1), window);
  if (!g) return null;
  const replicated = isReplicatedWeek(week);
  return (
    <Box
      title={driverWeekTitle(week)}
      data-testid={`driver-week-${week.weekStart}`}
      sx={{
        position: 'absolute',
        left: `${g.left}%`,
        width: `${g.width}%`,
        top: 2,
        height: DRIVER_ROW_HEIGHT - 4,
        boxSizing: 'border-box',
        px: 0.5,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        fontSize: '0.68rem',
        lineHeight: `${DRIVER_ROW_HEIGHT - 6}px`,
        border: '1px solid',
        borderRadius: 0.5,
        bgcolor: 'background.paper',
        ...(replicated
          ? { borderColor: 'divider', borderStyle: 'dotted', color: 'text.secondary', fontStyle: 'italic' }
          : { borderColor: 'info.main', color: 'text.primary' }),
        zIndex: 1,
      }}
    >
      {driverWeekText(week)}
    </Box>
  );
};

function barTitle(item: PlanItem, look: TimelineLook = NEW_LOOK): string {
  const lines = [
    `${item.kind === 'assignment' ? 'Obsadzenie' : 'Wariant'}: ${item.number ?? `#${item.orderId}`}`,
    routeText(item),
    `Okno zlecenia: ${shortDateTime(item.windowFrom)} – ${shortDateTime(item.windowTo)}`,
    `${item.occupancyBasis === 'estimate' ? 'Zajętość (szacunek)' : 'Okno zlecenia (trasa nieznana)'}: ` +
      `${shortDateTime(item.occupancyFrom)} – ${shortDateTime(item.occupancyTo)}`,
  ];
  // „Zajęty do” ze źródłem (12.10.2026) — plan TMS, gdy kończy później.
  const until = look.tmsPlan ? occupiedUntilLine(item) : null;
  if (until) lines.push(until);
  lines.push(`Stan: ${planRowStatus(item).label}`);
  const status = tmsStatusText(item);
  if (status) lines.push(status);
  if (item.assignedElsewhere) lines.push('Zlecenie jest już obsadzone innym pojazdem.');
  lines.push(...segmentNotesLines(item));
  return lines.join('\n');
}

/**
 * Pełny pasek obsadzenia — kolor z linii. Od 07.10.2026 jak w prototypie
 * karty pojazdu p. Mariusza: w realizacji niebieski, zaplanowane zielone,
 * zrealizowane szare (wcześniej zaplanowane szare, zrealizowane zielone) —
 * karta pojazdu i harmonogram floty mówią tym samym kolorem to samo.
 */
export const PHASE_FILL: Record<PlanPhase, { bgcolor: string; borderColor: string; color: string }> = {
  done: { bgcolor: 'grey.600', borderColor: 'grey.700', color: 'common.white' },
  inProgress: { bgcolor: 'primary.main', borderColor: 'primary.dark', color: 'primary.contrastText' },
  planned: { bgcolor: 'success.main', borderColor: 'success.dark', color: 'success.contrastText' },
};

/**
 * Kolory sprzed prototypu (do 07.10.2026) — przełącznik `vehicleCardPrototype`
 * wyłączony: zrealizowane zielone, zaplanowane szare.
 */
export const PHASE_FILL_LEGACY: Record<PlanPhase, { bgcolor: string; borderColor: string; color: string }> = {
  done: { bgcolor: 'success.main', borderColor: 'success.dark', color: 'success.contrastText' },
  inProgress: { bgcolor: 'primary.main', borderColor: 'primary.dark', color: 'primary.contrastText' },
  planned: { bgcolor: 'grey.500', borderColor: 'grey.600', color: 'common.white' },
};

/** Kolory pełnego paska według wyglądu osi. */
export const phaseFill = (prototypeColors: boolean) => (prototypeColors ? PHASE_FILL : PHASE_FILL_LEGACY);

/** Odcienie koloru linii — do odcinków paska. */
export function phaseTones(
  theme: Theme,
  phase: PlanPhase,
  prototypeColors = true,
): { main: string; dark: string } {
  if (!prototypeColors) {
    // Sprzed 07.10.2026: zrealizowane zielone, zaplanowane szare.
    if (phase === 'done') return { main: theme.palette.success.main, dark: theme.palette.success.dark };
    if (phase === 'inProgress') return { main: theme.palette.primary.main, dark: theme.palette.primary.dark };
    return { main: theme.palette.grey[500], dark: theme.palette.grey[700] };
  }
  if (phase === 'done') return { main: theme.palette.grey[600], dark: theme.palette.grey[800] };
  if (phase === 'inProgress') return { main: theme.palette.primary.main, dark: theme.palette.primary.dark };
  return { main: theme.palette.success.main, dark: theme.palette.success.dark };
}

/**
 * Obramowanie wariantu: przerywane w kolorze zaplanowanych, a wariant
 * zlecenia obsadzonego innym pojazdem — czerwone (konflikt). Pomarańcz jest
 * zarezerwowany dla ostrzeżeń (prototyp, zmiana 4; do 07.10.2026 wariant
 * miał obramowanie pomarańczowe).
 */
export const variantBorder = (item: Pick<PlanItem, 'assignedElsewhere'>, prototypeColors = true) =>
  item.assignedElsewhere ? 'error.main' : prototypeColors ? 'success.dark' : 'warning.main';

/**
 * Wygląd odcinka w kolorze linii: przejazd — pełny kolor, załadunek
 * i rozładunek — ciemniejszy, punkty pośrednie — ciemniejszy w pionowe
 * prążki, dolot — jasny z kreskowaną ramką, przerwa i odpoczynek — paski
 * ukośne (odpoczynek gęstszy i ciemniejszy, w drugą stronę).
 */
export function segmentFill(theme: Theme, phase: PlanPhase, kind: PlanSegmentKind, prototypeColors = true) {
  const t = phaseTones(theme, phase, prototypeColors);
  const paper = theme.palette.background.paper;
  switch (kind) {
    case 'access':
      return { backgroundColor: alpha(t.main, 0.28), border: `1px dashed ${t.dark}` };
    case 'loading':
    case 'unloading':
      return { backgroundColor: t.dark };
    case 'stop':
      return {
        backgroundColor: t.dark,
        backgroundImage: `repeating-linear-gradient(90deg, ${t.dark} 0 3px, ${t.main} 3px 5px)`,
      };
    case 'break':
      return {
        backgroundColor: paper,
        backgroundImage: `repeating-linear-gradient(45deg, ${t.main} 0 2px, ${paper} 2px 4px)`,
      };
    case 'rest':
      return {
        backgroundColor: paper,
        backgroundImage: `repeating-linear-gradient(-45deg, ${t.dark} 0 3px, ${paper} 3px 7px)`,
      };
    default:
      return { backgroundColor: t.main };
  }
}

/**
 * Odcinek „wg planu TMS” (12.10.2026, decyzja użytkownika): plan TMS
 * kończy zlecenie później niż szacunek. Stonowany — szare pionowe kreski na
 * tle papieru, kropkowana ramka — bo to informacja z TMS, nie nasz
 * szacunek ani ostrzeżenie (pomarańcz zostaje dla ostrzeżeń).
 */
export function tmsPlanFill(theme: Theme) {
  const grey = theme.palette.grey[500];
  const paper = theme.palette.background.paper;
  return {
    backgroundColor: paper,
    backgroundImage: `repeating-linear-gradient(90deg, ${alpha(grey, 0.55)} 0 2px, ${paper} 2px 6px)`,
    border: `1px dotted ${theme.palette.grey[600]}`,
  };
}

/** Odcinek „wg planu TMS” za paskiem szacunku; bez odcinka — nic. */
export const TmsPlanBar = ({
  item,
  window,
  top,
  height,
  orderTitle,
}: {
  item: PlanItem;
  window: TimelineWindow;
  top: number;
  height: number;
  orderTitle: string;
}) => {
  const seg = item.tmsPlanSegment;
  if (!seg) return null;
  const g = barGeometry(seg.start, seg.end, window, 0);
  if (!g || g.width <= 0) return null;
  return (
    <Link
      to={`/user-offers/${item.orderId}/show`}
      tabIndex={-1}
      title={tmsPlanTitle(seg, orderTitle)}
      data-testid={`plan-tms-${item.kind}-${item.orderId}`}
      data-kind="tmsPlan"
      sx={[
        {
          position: 'absolute',
          display: 'block',
          left: `${g.left}%`,
          width: `${g.width}%`,
          top,
          height,
          boxSizing: 'border-box',
          borderRadius: '0 4px 4px 0',
          zIndex: 1,
          opacity: item.kind === 'candidate' ? 0.6 : 1,
          '&:hover': { zIndex: 3, boxShadow: 2 },
        },
        (theme: Theme) => tmsPlanFill(theme),
      ]}
    />
  );
};

function barLook(item: PlanItem, look: TimelineLook) {
  if (item.kind === 'candidate') {
    return {
      bgcolor: 'background.paper',
      borderStyle: 'dashed',
      borderColor: variantBorder(item, look.prototypeColors),
      color: 'text.primary',
    };
  }
  return {
    ...phaseFill(look.prototypeColors)[planPhase(item)],
    // Bez trasy pasek to całe okno zlecenia — jaśniej, bo to nie szacunek.
    opacity: item.occupancyBasis === 'window' ? 0.7 : 1,
  };
}

/** Okno zlecenia od klienta — szeroki obszar linią przerywaną pod paskiem. */
const OrderWindow = ({
  item,
  window,
  top,
  height,
}: {
  item: PlanItem;
  window: TimelineWindow;
  top: number;
  height: number;
}) => {
  const g = barGeometry(item.windowFrom, item.windowTo, window);
  if (!g) return null;
  return (
    <Box
      data-testid={`plan-window-${item.kind}-${item.orderId}`}
      sx={{
        position: 'absolute',
        left: `${g.left}%`,
        width: `${g.width}%`,
        top,
        height,
        boxSizing: 'border-box',
        border: '1px dashed',
        borderColor: 'text.disabled',
        borderLeftStyle: g.clippedStart ? 'none' : 'dashed',
        borderRightStyle: g.clippedEnd ? 'none' : 'dashed',
        borderRadius: 1,
        // Okno nie jest klikalne — dymek i odnośnik ma pasek.
        pointerEvents: 'none',
      }}
    />
  );
};

const PlanBar = ({
  item,
  window,
  top,
  height,
  font,
  fontPx,
  trackWidth,
  look,
}: {
  item: PlanItem;
  window: TimelineWindow;
  top: number;
  height: number;
  font: string;
  fontPx: number;
  trackWidth: number;
  look: TimelineLook;
}) => {
  const g = barGeometry(item.occupancyFrom, item.occupancyTo, window);
  if (!g) return null;
  const label = barLabelParts(g, item, item.kind === 'assignment' ? '' : ' · wariant', trackWidth, fontPx);
  return (
    <>
      <Link
        to={`/user-offers/${item.orderId}/show`}
        title={barTitle(item, look)}
        data-testid={`plan-bar-${item.kind}-${item.orderId}`}
        data-phase={planPhase(item)}
        sx={{
          position: 'absolute',
          display: 'block',
          left: `${g.left}%`,
          width: `${g.width}%`,
          top,
          height,
          boxSizing: 'border-box',
          px: 0.5,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          fontSize: font,
          lineHeight: `${height - 2}px`,
          textDecoration: 'none',
          borderRadius: 1,
          borderTopLeftRadius: g.clippedStart ? 0 : undefined,
          borderBottomLeftRadius: g.clippedStart ? 0 : undefined,
          borderTopRightRadius: g.clippedEnd ? 0 : undefined,
          borderBottomRightRadius: g.clippedEnd ? 0 : undefined,
          border: '1px solid',
          ...barLook(item, look),
          zIndex: 1,
          '&:hover': { zIndex: 2, boxShadow: 2 },
        }}
      >
        {label.inside}
      </Link>
      {label.outside ? (
        <OutsideLabel
          label={label.outside}
          top={top}
          height={height}
          font={font}
          testId={`plan-bar-outside-label-${item.kind}-${item.orderId}`}
        />
      ) : null}
    </>
  );
};

/**
 * Pasek rozpisany na odcinki. Każdy odcinek prowadzi do zlecenia i ma własny
 * dymek; numer zlecenia leży nad paskiem zajętości i nie przechwytuje
 * kursora, więc dymek pokazuje odcinek, nad którym stoi mysz. Wariant —
 * odcinki przygaszone, obramowanie przerywane jak dotąd.
 */
const SegmentedBar = ({
  item,
  window,
  top,
  height,
  font,
  fontPx,
  trackWidth,
  look,
}: {
  item: PlanItem;
  window: TimelineWindow;
  top: number;
  height: number;
  font: string;
  fontPx: number;
  trackWidth: number;
  look: TimelineLook;
}) => {
  const phase = planPhase(item);
  const variant = item.kind === 'candidate';
  const orderTitle = barTitle(item, look);
  const label = barGeometry(item.occupancyFrom, item.occupancyTo, window);
  const text = label ? barLabelParts(label, item, variant ? ' · wariant' : '', trackWidth, fontPx) : null;
  return (
    <>
      {(item.segments ?? []).map((s, idx) => {
        const g = barGeometry(s.start, s.end, window, 0);
        if (!g || g.width <= 0) return null;
        return (
          <Link
            // Odcinki pozycji przychodzą zawsze w tej samej kolejności.
            key={`${s.kind}-${s.start}`}
            to={`/user-offers/${item.orderId}/show`}
            title={segmentTitle(s, orderTitle)}
            data-testid={`plan-segment-${item.kind}-${item.orderId}-${idx}`}
            data-kind={s.kind}
            sx={[
              {
                position: 'absolute',
                display: 'block',
                left: `${g.left}%`,
                width: `${g.width}%`,
                top,
                height,
                boxSizing: 'border-box',
                zIndex: 1,
                opacity: variant ? 0.5 : 1,
                '&:hover': { zIndex: 3, boxShadow: 2, opacity: 1 },
              },
              (theme: Theme) => segmentFill(theme, phase, s.kind, look.prototypeColors),
            ]}
          />
        );
      })}
      {label ? (
        <Box
          data-testid={`plan-bar-${item.kind}-${item.orderId}`}
          data-phase={phase}
          data-segmented="true"
          sx={{
            position: 'absolute',
            left: `${label.left}%`,
            width: `${label.width}%`,
            top,
            height,
            boxSizing: 'border-box',
            px: 0.5,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            fontSize: font,
            lineHeight: `${height - 2}px`,
            borderRadius: 1,
            pointerEvents: 'none',
            zIndex: 2,
            ...(variant
              ? {
                  color: 'text.primary',
                  border: '1px dashed',
                  borderColor: variantBorder(item, look.prototypeColors),
                }
              : { color: 'common.white', textShadow: '0 0 3px rgba(0,0,0,0.8)' }),
          }}
        >
          {text?.inside}
        </Box>
      ) : null}
      {text?.outside ? (
        <OutsideLabel
          label={text.outside}
          top={top}
          height={height}
          font={font}
          testId={`plan-bar-outside-label-${item.kind}-${item.orderId}`}
        />
      ) : null}
    </>
  );
};

interface Band {
  phase: PlanPhase | null;
  items: PlanItem[];
  lanes: number[];
  top: number;
  height: number;
}

/**
 * Pasma wiersza: jedno (wiersz karty auta to już jedna linia) albo trzy
 * linie faz (wiersz auta we flocie). Pasmo ma co najmniej jeden tor, żeby
 * linie wierszy floty stały na tej samej wysokości.
 */
function rowBands(row: TimelineRow, lane: number, withBrackets = false): Band[] {
  const groups: Array<{ phase: PlanPhase | null; items: PlanItem[] }> = row.splitPhases
    ? PLAN_PHASES.map(({ phase }) => ({ phase, items: row.items.filter((i) => planPhase(i) === phase) }))
    : [{ phase: null, items: row.items }];
  let top = 0;
  return groups.map(({ phase, items }) => {
    // Pozycja bez dat i tak nie ma paska — zerowy odcinek nie zajmuje toru.
    // Nawiasy Z/R (24.10.2026) — okno z planu TMS bywa szersze niż okno zlecenia.
    const spans = items.map((i) => {
      const span = planItemSpan(i) ?? { start: 0, end: 0 };
      const extra = withBrackets ? bracketsExtent(i) : [];
      return extra.length === 0
        ? span
        : { start: Math.min(span.start, ...extra), end: Math.max(span.end, ...extra) };
    });
    const { lanes, count } = packLanes(spans);
    const height = LANE_GAP + count * (lane + LANE_GAP);
    const band = { phase, items, lanes, top, height };
    top += height;
    return band;
  });
}

export const PlanTimeline = ({
  window,
  rows,
  now = new Date(),
  labelWidth = 200,
  dayMinWidth = 56,
  labelHeader = '',
  size = 'large',
  extraColumns = [],
  overrun = false,
}: {
  window: TimelineWindow;
  rows: TimelineRow[];
  now?: Date;
  labelWidth?: number;
  dayMinWidth?: number;
  labelHeader?: ReactNode;
  size?: TimelineSize;
  /** Kolumny po prawej stronie osi (harmonogram floty wg makiety, 15.10.2026). */
  extraColumns?: TimelineColumn[];
  /** Odcinek spóźnienia za oknem rozładunku (`overrunUntil`). */
  overrun?: boolean;
}) => {
  const dims = SIZES[size];
  const look = useTimelineLook();
  // Szerokość osi w px — podpisy pasków dopasowane do miejsca (29.10.2026).
  const [trackRef, trackWidth] = useElementWidth<HTMLDivElement>();
  // Nawiasy okien klienta przy każdym zleceniu (24.10.2026, S-24.10-05).
  const brackets = useFeature(GANTT_WINDOW_BRACKETS);
  const days = timelineDays(window);
  const dayBoxes = days.map((d) => {
    const g = barGeometry(d.start, d.end, window);
    return { ...d, left: g?.left ?? 0, width: g?.width ?? 0 };
  });
  const nowPct = nowOffset(now, window);
  const extraWidth = extraColumns.reduce((sum, c) => sum + c.width, 0);
  const minWidth = labelWidth + days.length * dayMinWidth + extraWidth;
  const extraTemplate = extraColumns.map((c) => ` ${c.width}px`).join('');

  const dayBackground = (
    <>
      {dayBoxes.map((d) => (
        <Box
          key={d.key}
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: `${d.left}%`,
            width: `${d.width}%`,
            borderLeft: '1px solid',
            borderColor: 'divider',
            bgcolor: d.weekend ? 'action.hover' : undefined,
          }}
        />
      ))}
      {nowPct !== null ? (
        <Box
          data-testid="plan-now"
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: `${nowPct}%`,
            width: '2px',
            bgcolor: 'error.main',
            zIndex: 3,
          }}
        />
      ) : null}
    </>
  );

  return (
    <Box sx={{ overflowX: 'auto', width: 0, minWidth: '100%' }} data-testid="plan-scroll">
      <Box sx={{ minWidth, display: 'grid', gridTemplateColumns: `${labelWidth}px minmax(0, 1fr)${extraTemplate}` }}>
        <Box
          sx={{
            position: 'sticky',
            left: 0,
            zIndex: 4,
            bgcolor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
            px: 1,
            display: 'flex',
            alignItems: 'flex-end',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            {labelHeader}
          </Typography>
        </Box>
        <Box ref={trackRef} sx={{ position: 'relative', height: 36, borderBottom: '1px solid', borderColor: 'divider' }}>
          {dayBoxes.map((d) => (
            <Typography
              key={d.key}
              variant="caption"
              sx={{
                position: 'absolute',
                left: `${d.left}%`,
                width: `${d.width}%`,
                top: 2,
                lineHeight: 1.25,
                textAlign: 'center',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                color: d.weekend ? 'text.secondary' : 'text.primary',
                borderLeft: '1px solid',
                borderColor: 'divider',
              }}
            >
              {/* Dwie linie — w jednej „pn 29.09” nachodzi na sąsiada przy 4 tygodniach. */}
              {d.weekday}
              <br />
              {d.date}
            </Typography>
          ))}
        </Box>
        {extraColumns.map((c) => (
          <Box
            key={c.key}
            data-testid={`plan-column-${c.key}`}
            sx={{
              borderBottom: '1px solid',
              borderLeft: '1px solid',
              borderColor: 'divider',
              px: 1,
              display: 'flex',
              alignItems: 'flex-end',
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
              {c.header}
            </Typography>
          </Box>
        ))}
        {rows.map((row) => {
          const bands = row.driverWeeks ? [] : rowBands(row, dims.lane, brackets);
          const height = row.driverWeeks
            ? DRIVER_ROW_HEIGHT
            : bands.reduce((sum, b) => sum + b.height, 0);
          return (
            <Box key={row.key} sx={{ display: 'contents' }} data-testid={`plan-row-${row.key}`}>
              <Box
                sx={{
                  position: 'sticky',
                  left: 0,
                  zIndex: 4,
                  bgcolor: 'background.paper',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  px: 1,
                  py: 0.25,
                  minHeight: height,
                  overflow: 'hidden',
                }}
              >
                {row.label}
              </Box>
              <Box
                sx={{
                  position: 'relative',
                  minHeight: height,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                {dayBackground}
                {(row.gaps ?? []).map((gap) => (
                  <GapArea key={gap.key} gap={gap} window={window} />
                ))}
                {(row.months ?? [])
                  .filter((m) => m.unavailable)
                  .map((m) => {
                    const g = barGeometry(m.from, m.to, window);
                    return g ? (
                      <Box
                        key={m.id}
                        data-testid={`plan-month-${m.id}`}
                        title={unavailableMonthTitle(m.status)}
                        sx={{
                          position: 'absolute',
                          top: 0,
                          bottom: 0,
                          left: `${g.left}%`,
                          width: `${g.width}%`,
                          backgroundImage:
                            'repeating-linear-gradient(45deg, rgba(237,108,2,0.18) 0 6px, transparent 6px 12px)',
                        }}
                      />
                    ) : null;
                  })}
                {bands.map((band, b) => (
                  <Box
                    key={band.phase ?? 'all'}
                    data-testid={band.phase ? `plan-band-${row.key}-${band.phase}` : undefined}
                    sx={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      top: band.top,
                      height: band.height,
                      // Linie wiersza auta rozdziela kropkowana kreska.
                      ...(row.splitPhases && b > 0 ? { borderTop: '1px dotted', borderColor: 'divider' } : {}),
                    }}
                  >
                    {band.items.map((item, i) => {
                      const laneTop = LANE_GAP + band.lanes[i] * (dims.lane + LANE_GAP);
                      return (
                        <Fragment key={`${item.kind}-${item.orderId}`}>
                          {brackets && item.windowBrackets ? (
                            <WindowBrackets
                              item={item}
                              window={window}
                              barTop={laneTop + dims.inset}
                              barHeight={dims.lane - 2 * dims.inset}
                              hook={dims.inset}
                              tipAttr="title"
                              labels={false}
                            />
                          ) : (
                            <OrderWindow item={item} window={window} top={laneTop} height={dims.lane} />
                          )}
                          {look.tmsPlan ? (
                            <TmsPlanBar
                              item={item}
                              window={window}
                              top={laneTop + dims.inset}
                              height={dims.lane - 2 * dims.inset}
                              orderTitle={barTitle(item, look)}
                            />
                          ) : null}
                          {overrun ? (
                            <LateBar
                              item={item}
                              window={window}
                              top={laneTop + dims.inset}
                              height={dims.lane - 2 * dims.inset}
                            />
                          ) : null}
                          {hasSegments(item) ? (
                            <SegmentedBar
                              item={item}
                              window={window}
                              top={laneTop + dims.inset}
                              height={dims.lane - 2 * dims.inset}
                              font={dims.font}
                              fontPx={dims.fontPx}
                              trackWidth={trackWidth}
                              look={look}
                            />
                          ) : (
                            <PlanBar
                              item={item}
                              window={window}
                              top={laneTop + dims.inset}
                              height={dims.lane - 2 * dims.inset}
                              font={dims.font}
                              fontPx={dims.fontPx}
                              trackWidth={trackWidth}
                              look={look}
                            />
                          )}
                        </Fragment>
                      );
                    })}
                  </Box>
                ))}
                {(row.driverWeeks ?? []).map((week) => (
                  <DriverWeekSegment key={week.weekStart} week={week} window={window} />
                ))}
              </Box>
              {extraColumns.map((c, i) => (
                <Box
                  key={c.key}
                  data-testid={`plan-cell-${row.key}-${c.key}`}
                  sx={{
                    borderBottom: '1px solid',
                    borderLeft: '1px solid',
                    borderColor: 'divider',
                    px: 1,
                    py: 0.25,
                    minHeight: height,
                    overflow: 'hidden',
                  }}
                >
                  {row.extra?.[i] ?? null}
                </Box>
              ))}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

/**
 * Legenda pod osią; `driverWeeks` — także tor kierowcy z harmonogramu,
 * `splitPhases` — opis trzech linii w wierszu auta (harmonogram floty),
 * `segments` — odcinki paska (gdy któraś pozycja je ma).
 */
export const PlanLegend = ({
  driverWeeks = false,
  splitPhases = false,
  segments = false,
  tmsPlan = false,
  fleetMockup = false,
}: {
  driverWeeks?: boolean;
  splitPhases?: boolean;
  segments?: boolean;
  /** Odcinek „wg planu TMS” (gdy któraś pozycja go ma). */
  tmsPlan?: boolean;
  /** Luka i spóźnienie — harmonogram floty wg makiety (15.10.2026). */
  fleetMockup?: boolean;
}) => {
  const look = useTimelineLook();
  const brackets = useFeature(GANTT_WINDOW_BRACKETS);
  return (
  <>
    <PhaseLegend
      driverWeeks={driverWeeks}
      splitPhases={splitPhases}
      prototypeColors={look.prototypeColors}
      brackets={brackets}
    />
    {fleetMockup ? (
      <Box data-testid="plan-legend-mockup" sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mt: 0.5 }}>
        <LegendSwatch
          sx={{
            bgcolor: (theme: Theme) => alpha(theme.palette.success.main, 0.06),
            borderStyle: 'dashed',
            borderColor: 'success.main',
            width: 24,
          }}
          label="luka w planie (kliknij, żeby zobaczyć pasujące zlecenia)"
        />
        <LegendSwatch
          sx={{
            backgroundImage: 'repeating-linear-gradient(135deg, #ed6c02 0 6px, #f7a65a 6px 12px)',
            borderColor: 'warning.dark',
            width: 24,
          }}
          label="spóźnienie wg szacunku — po końcu okna rozładunku"
        />
      </Box>
    ) : null}
    {tmsPlan && look.tmsPlan ? (
      <Box data-testid="plan-legend-tms" sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 0.5 }}>
        <LegendSwatch
          sx={[{ width: 24 }, (theme: Theme) => tmsPlanFill(theme)]}
          label="wg planu TMS — plan kończy zlecenie później niż szacunek (tylko informacja)"
        />
      </Box>
    ) : null}
    {segments ? (
      <Box
        data-testid="plan-legend-segments"
        sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mt: 0.5 }}
      >
        <Typography variant="caption" color="text.secondary">
          Odcinki szacunku (w kolorze linii):
        </Typography>
        {LEGEND_SEGMENTS.map(({ kind, label }) => (
          <LegendSwatch
            key={kind}
            sx={[{ border: 0, width: 24 }, (theme: Theme) => segmentFill(theme, 'inProgress', kind)]}
            label={label}
          />
        ))}
      </Box>
    ) : null}
  </>
  );
};

const PhaseLegend = ({
  driverWeeks,
  splitPhases,
  prototypeColors,
  brackets = false,
}: {
  driverWeeks: boolean;
  splitPhases: boolean;
  prototypeColors: boolean;
  /** Nawiasy Z/R zamiast linii przerywanej okna (24.10.2026). */
  brackets?: boolean;
}) => {
  const fill = phaseFill(prototypeColors);
  return (
  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mt: 1 }}>
    {splitPhases ? (
      <Typography variant="caption" color="text.secondary">
        W wierszu pojazdu trzy linie: zrealizowane, w realizacji, zaplanowane.
      </Typography>
    ) : null}
    {brackets ? (
      <BracketLegend />
    ) : (
      <LegendSwatch
        sx={{ bgcolor: 'transparent', borderStyle: 'dashed', borderColor: 'text.disabled', width: 28 }}
        label="okno zlecenia od klienta"
      />
    )}
    <LegendSwatch sx={fill.done} label="zrealizowane (szacunek)" />
    <LegendSwatch sx={fill.inProgress} label="w realizacji (szacunek)" />
    <LegendSwatch sx={fill.planned} label="zaplanowane (szacunek)" />
    <LegendSwatch
      sx={{
        bgcolor: 'background.paper',
        borderStyle: 'dashed',
        borderColor: prototypeColors ? 'success.dark' : 'warning.main',
      }}
      label="wariant (wstępne wskazanie)"
    />
    <LegendSwatch
      sx={{ bgcolor: 'background.paper', borderStyle: 'dashed', borderColor: 'error.main' }}
      label="wariant zlecenia obsadzonego innym pojazdem"
    />
    {driverWeeks ? (
      <>
        <LegendSwatch sx={{ bgcolor: 'background.paper', borderColor: 'info.main' }} label="kierowca wg harmonogramu" />
        <LegendSwatch
          sx={{ bgcolor: 'background.paper', borderStyle: 'dotted', borderColor: 'divider' }}
          label="kierowca przepisany z wcześniejszego miesiąca (tymczasowo)"
        />
      </>
    ) : null}
    <LegendSwatch sx={{ bgcolor: 'error.main', width: 2, border: 0 }} label="teraz" />
  </Box>
  );
};

/**
 * Legenda nawiasów Z/R (24.10.2026, S-24.10-05) — wspólna dla karty pojazdu
 * i harmonogramu floty.
 */
export const BracketLegend = () => {
  const line = (color: string) => `${BRACKET_LINE_PX}px solid ${color}`;
  const swatch = (color: string, top: boolean) => ({
    width: 22,
    height: 10,
    border: 0,
    borderRadius: 0,
    bgcolor: 'transparent',
    borderLeft: line(color),
    borderRight: line(color),
    ...(top ? { borderTop: line(color) } : { borderBottom: line(color) }),
  });
  return (
    <Box data-testid="legend-window-brackets" sx={{ display: 'contents' }}>
      <LegendSwatch sx={swatch(BRACKET_COLORS.loading, true)} label="Z — okno załadunku (u góry paska)" />
      <LegendSwatch sx={swatch(BRACKET_COLORS.unloading, false)} label="R — okno rozładunku (u dołu paska)" />
      <LegendSwatch sx={swatch(BRACKET_COLORS.conflict, true)} label="kolizja — szacunek poza oknem" />
    </Box>
  );
};

export const LegendSwatch = ({ sx, label }: { sx: SxProps<Theme>; label: string }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
    <Box
      sx={[
        { width: 18, height: 10, border: '1px solid', borderRadius: 0.5, boxSizing: 'border-box' },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Box>
);
